import { escape, omit } from 'lodash';

import type { DisplayConfigProps } from '~/components/core/Editor/types';
import NOTE_HTML_ALLOWED_IMAGE_TYPES from '~/server_shared/generated-types/NOTE_HTML_ALLOWED_IMAGE_TYPES';

/** Editor HTML for a plain-text note: one block per line, so the line breaks survive in the editor. */
export const plainTextToNoteHtml = (text: string): string =>
  text
    ? text
        .split(/\r?\n/)
        .map((line) => `<div>${line ? escape(line) : '<br>'}</div>`)
        .join('')
    : '';

// An image counts as content, as it does on the server, which saves it as `[image]` in the plain text.
export const hasNoteHtmlContent = (html: string | null | undefined): boolean => {
  const { body } = new DOMParser().parseFromString(html || '', 'text/html');
  return !!body.textContent?.trim() || !!body.querySelector('img');
};

// No link button and no alignment; links already in a note (e.g. an imported email signature) are
// kept. Images stay inline in the note - there is no per-note file storage to upload them to.
export const RICH_NOTE_EDITOR_DISPLAY_CONFIG: DisplayConfigProps = {
  headings: true,
  font: true,
  textColor: true,
  textStyle: true,
  lists: true,
  images: true,
  embedImages: NOTE_HTML_ALLOWED_IMAGE_TYPES,
  preserveLinks: true,
};

// A note template offers the note editor's formatting, without images: a pasted image would be stored
// in the template and copied into every note made from it, and no other template type takes images.
export const RICH_NOTE_TEMPLATE_DISPLAY_CONFIG: DisplayConfigProps = omit(RICH_NOTE_EDITOR_DISPLAY_CONFIG, [
  'images',
  'embedImages',
]);
